<script lang="ts">
import { defineComponent, computed, h, type VNode, type VNodeChild } from 'vue';

const LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const IMG_PROTOCOLS = new Set(['http:', 'https:']);
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

// ---- URL sanitizers ----
function safeHref(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  if (!v) return undefined;

  // Block protocol-relative URLs
  if (v.startsWith('//')) return undefined;

  // relative or hash
  if (v.startsWith('#') || v.startsWith('/') || v.startsWith('./') || v.startsWith('../')) return v;

  // no scheme => treat as relative-ish
  if (!SCHEME_RE.test(v)) return v;

  try {
    const u = new URL(v);
    return LINK_PROTOCOLS.has(u.protocol) ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

function safeImgSrc(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  if (!v) return undefined;

  // Block protocol-relative URLs
  if (v.startsWith('//')) return undefined;

  // Allow only safe raster data URIs (block svg)
  const lower = v.toLowerCase();
  if (lower.startsWith('data:image/')) {
    if (lower.startsWith('data:image/svg')) return undefined;
    return v;
  }

  if (v.startsWith('/') || v.startsWith('./') || v.startsWith('../')) return v;
  if (!SCHEME_RE.test(v)) return v;

  try {
    const u = new URL(v);
    return IMG_PROTOCOLS.has(u.protocol) ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

// ---- STYLE sanitization (NEW) ----
// We only support a small safe subset of inline styles (primarily CKEditor colors).
const ALLOWED_STYLE_PROPS = new Set([
  'color',
  'background-color',
  '--ck-content-list-marker-color', // used by CKEditor list markers
]);

// Only allow common safe color syntaxes.
// (Blocks url(), expression(), var(), and other CSS injection vectors.)
const COLOR_RE =
  /^(#[0-9a-f]{3,8}|rgba?\([^()]{1,64}\)|hsla?\([^()]{1,64}\)|transparent|currentColor)$/i;

function safeStyle(el: Element): Record<string, string> | undefined {
  const styleAttr = el.getAttribute('style');
  if (!styleAttr) return undefined;

  const out: Record<string, string> = {};
  for (const decl of styleAttr.split(';')) {
    const idx = decl.indexOf(':');
    if (idx === -1) continue;

    const prop = decl.slice(0, idx).trim().toLowerCase();
    const val = decl.slice(idx + 1).trim();

    if (!ALLOWED_STYLE_PROPS.has(prop)) continue;

    if (
      (prop === 'color' ||
        prop === 'background-color' ||
        prop === '--ck-content-list-marker-color') &&
      !COLOR_RE.test(val)
    ) {
      continue;
    }

    out[prop] = val;
  }

  return Object.keys(out).length ? out : undefined;
}

// ---- Renderer ----
function renderNode(node: Node, depth = 0): VNodeChild[] {
  // basic depth guard
  if (depth > 60) return [];

  // TEXT
  if (node.nodeType === Node.TEXT_NODE) {
    // Preserve whitespace nodes to avoid "HelloWorld" bugs.
    return [node.textContent ?? ''];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return [];

  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  const children: VNodeChild[] = [];
  for (const child of Array.from(el.childNodes)) {
    children.push(...renderNode(child, depth + 1));
  }

  switch (tag) {
    case 'p':
      return [h('p', { class: 'html-paragraph' }, children)];

    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      return [h(tag, { class: 'html-heading' }, children)];

    case 'strong':
    case 'b':
      return [h('strong', { class: 'html-strong' }, children)];

    case 'em':
    case 'i':
      return [h('em', { class: 'html-em' }, children)];

    case 'u':
      return [h('u', { class: 'html-underline' }, children)];

    case 'br':
      return [h('br')];

    case 'ul':
    case 'ol':
      return [h(tag, { class: 'html-list' }, children)];

    case 'li':
      // NEW: allow safe inline styles (for CKEditor marker color variable)
      return [h('li', { class: 'html-list-item', style: safeStyle(el) }, children)];

    case 'a': {
      const href = safeHref(el.getAttribute('href'));
      if (!href) return [h('span', { class: 'html-link html-link--unsafe' }, children)];

      const isExternal = /^https?:\/\//i.test(href);
      return [
        h(
          'a',
          {
            class: 'html-link',
            href,
            target: isExternal ? '_blank' : undefined,
            rel: isExternal ? 'noopener noreferrer' : undefined,
          },
          children
        ),
      ];
    }

    case 'img': {
      const src = safeImgSrc(el.getAttribute('src'));
      if (!src) {
        const alt = el.getAttribute('alt') ?? '';
        return alt ? [h('span', { class: 'html-image html-image--unsafe' }, alt)] : [];
      }
      return [
        h('img', {
          class: 'html-image',
          src,
          alt: el.getAttribute('alt') ?? '',
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
          title: el.getAttribute('title') || undefined,
          loading: 'lazy',
        }),
      ];
    }

    case 'code':
      return [h('code', { class: 'html-code' }, children)];

    case 'pre':
      return [h('pre', { class: 'html-pre' }, children)];

    case 'blockquote':
      return [h('blockquote', { class: 'html-blockquote' }, children)];

    case 'table':
      return [h('table', { class: 'html-table' }, children)];
    case 'thead':
      return [h('thead', {}, children)];
    case 'tbody':
      return [h('tbody', {}, children)];
    case 'tr':
      return [h('tr', {}, children)];
    case 'th':
      return [h('th', { class: 'html-table-header' }, children)];
    case 'td':
      return [h('td', { class: 'html-table-cell' }, children)];

    case 'hr':
      return [h('hr', { class: 'html-hr' })];

    case 'div':
      return children.length ? [h('div', { class: 'html-div' }, children)] : [];

    case 'span':
      // NEW: allow safe inline styles (color + background-color)
      return children.length
        ? [h('span', { class: 'html-span', style: safeStyle(el) }, children)]
        : [];

    default:
      // Unknown tags: render children only
      return children;
  }
}

export default defineComponent({
  name: 'HtmlRenderer',
  props: {
    content: { type: String, required: true },
  },
  setup(props) {
    const nodes = computed<VNodeChild[]>(() => {
      const html = props.content;
      if (!html.trim()) return [];

      // SSR guard
      if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        return [html];
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const body = doc.body;
      const out: VNodeChild[] = [];

      for (const child of Array.from(body.childNodes)) {
        out.push(...renderNode(child));
      }
      return out;
    });

    return (): VNode => h('div', { class: 'html-content' }, nodes.value);
  },
});
</script>

<style scoped>
.html-content {
  font-size: 14px;
  line-height: 1.6;
}

.html-content :deep(.html-heading) {
  margin: 1em 0 0.5em;
  font-weight: 600;
  color: #2c3e50; /* NOTE: headings will not inherit span color */
}

.html-content :deep(.html-heading:first-child) {
  margin-top: 0;
}

.html-content :deep(h1) {
  font-size: 1.8em;
}

.html-content :deep(h2) {
  font-size: 1.5em;
}

.html-content :deep(h3) {
  font-size: 1.3em;
}

.html-content :deep(.html-paragraph) {
  margin: 0.8em 0;
}

.html-content :deep(.html-list) {
  padding-left: 1.5em;
  margin: 0.8em 0;
}

.html-content :deep(.html-list-item) {
  margin: 0.3em 0;
}

/* NEW: Support CKEditor list marker color variable */
.html-content :deep(.html-list-item)::marker {
  color: var(--ck-content-list-marker-color, currentColor);
}

.html-content :deep(.html-code) {
  background-color: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
}

.html-content :deep(.html-pre) {
  background-color: #f5f5f5;
  padding: 1em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1em 0;
}

.html-content :deep(.html-pre .html-code) {
  background: none;
  padding: 0;
}

.html-content :deep(.html-blockquote) {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding-left: 1em;
  color: #666;
  font-style: italic;
}

.html-content :deep(.html-table) {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}

.html-content :deep(.html-table-header),
.html-content :deep(.html-table-cell) {
  border: 1px solid #ddd;
  padding: 0.5em;
}

.html-content :deep(.html-table-header) {
  background-color: #f5f5f5;
  font-weight: 600;
}

/* CHANGED: don't override user-provided text color inside spans */
.html-content :deep(.html-strong) {
  color: inherit;
  font-weight: 600;
}

.html-content :deep(.html-link) {
  color: #2080f0;
  text-decoration: none;
}

.html-content :deep(.html-link:hover) {
  text-decoration: underline;
}

.html-content :deep(.html-link--unsafe) {
  color: inherit;
  text-decoration: underline dotted;
  cursor: default;
}

.html-content :deep(.html-image) {
  max-width: 100%;
  height: auto;
  margin: 1em 0;
}

.html-content :deep(.html-image--unsafe) {
  font-style: italic;
  color: #999;
}

.html-content :deep(.html-hr) {
  border: none;
  border-top: 1px solid #ddd;
  margin: 1em 0;
}

.html-content :deep(.html-underline) {
  text-decoration: underline;
}
</style>
